import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export type AuthProfile = {
  user_id: string;
  email?: string | null;
  display_name?: string | null;
  onboarding_complete?: boolean | null;
  onboarding_completed?: boolean | null;
  ritmos?: unknown;
  ritmos_seleccionados?: unknown;
  zonas?: unknown;
  rol_baile?: string | null;
};

const PROFILE_SELECT =
  'user_id, email, display_name, onboarding_complete, onboarding_completed, ritmos, ritmos_seleccionados, zonas, rol_baile';

function countValues(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  if (typeof value !== 'string') return 0;
  const trimmed = value.trim();
  if (!trimmed) return 0;
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed.length : 1;
  } catch {
    return 1;
  }
}

export function hasCompleteOnboarding(profile: AuthProfile | null | undefined): boolean {
  if (!profile) return false;
  if (profile.onboarding_complete === true || profile.onboarding_completed === true) return true;

  const hasName = Boolean(profile.display_name?.trim());
  const hasRitmos = countValues(profile.ritmos) + countValues(profile.ritmos_seleccionados) > 0;
  const hasZonas = countValues(profile.zonas) > 0;
  const hasRol = Boolean(profile.rol_baile?.trim());

  return hasName && hasRitmos && hasZonas && hasRol;
}

export async function ensureUserProfile(
  user: Pick<User, 'id' | 'email' | 'user_metadata'>
): Promise<AuthProfile | null> {
  const { data: existingProfile, error: readError } = await supabase
    .from('profiles_user')
    .select(PROFILE_SELECT)
    .eq('user_id', user.id)
    .maybeSingle();

  if (readError) throw readError;
  if (existingProfile) return existingProfile as AuthProfile;

  const metadata = user.user_metadata ?? {};
  const displayName =
    (typeof metadata.full_name === 'string' && metadata.full_name.trim()) ||
    (typeof metadata.name === 'string' && metadata.name.trim()) ||
    user.email?.split('@')[0] ||
    'Usuario';

  const { data: insertedProfile, error: insertError } = await supabase
    .from('profiles_user')
    .insert({
      user_id: user.id,
      email: user.email ?? null,
      display_name: displayName,
      onboarding_complete: false,
      onboarding_completed: false,
    })
    .select(PROFILE_SELECT)
    .single();

  if (!insertError) return insertedProfile as AuthProfile;

  if (insertError.code === '23505') {
    const { data: racedProfile, error: rereadError } = await supabase
      .from('profiles_user')
      .select(PROFILE_SELECT)
      .eq('user_id', user.id)
      .maybeSingle();

    if (rereadError) throw rereadError;
    return racedProfile as AuthProfile | null;
  }

  throw insertError;
}
